/**
 * TodoListModals - Bundle of the five task-list modals rendered on the To-Do
 * screen. Keeping them in one composite component lets app/(tabs)/todo.tsx
 * stay under the global 500-line ceiling while preserving per-modal state
 * ownership at the screen level.
 */

import React from 'react';

import { CreateListSheet } from './CreateListSheet';
import { MoveToListModal } from './MoveToListModal';
import { ManageListsModal } from './ManageListsModal';
import { ShareListMemberPicker } from './ShareListMemberPicker';
import { DeleteListConfirmModal } from './DeleteListConfirmModal';
import type { createMoveTaskCoordinator } from './MoveTaskCoordinator';
import type { ListDeletionCoordinator } from '@/lib/client/ListDeletionCoordinator';
import type { Task } from '@/types/tasks';
import type {
  TaskList,
  UpdateTaskListInput,
  DeleteTaskListAction,
} from '@/types/task-lists';

type Coordinator = ReturnType<typeof createMoveTaskCoordinator>;

type TodoListModalsProps = {
  lists: TaskList[];
  activeListId: string | null;
  defaultListId: string | null;
  currentUserId: string | null;
  atCap: boolean;

  activeTasks: Task[];
  completedTasks: Task[];
  coordinator: Coordinator;
  deletionCoordinator: ListDeletionCoordinator | null;

  createListVisible: boolean;
  setCreateListVisible: (visible: boolean) => void;

  moveTarget: Task | null;
  setMoveTarget: (task: Task | null) => void;

  manageVisible: boolean;
  setManageVisible: (visible: boolean) => void;

  shareTarget: TaskList | null;
  setShareTarget: (list: TaskList | null) => void;

  deleteTarget: TaskList | null;
  setDeleteTarget: (list: TaskList | null) => void;

  setActiveListId: (id: string) => void;
  setHighlightTaskId: (id: string | null) => void;

  createList: (input: { name: string }) => Promise<TaskList>;
  updateList: (id: string, patch: UpdateTaskListInput) => Promise<TaskList>;
  deleteList: (id: string, action: DeleteTaskListAction) => Promise<void>;
  reorderLists: (ids: string[]) => Promise<void>;
};

export function TodoListModals({
  lists,
  activeListId,
  defaultListId,
  currentUserId,
  atCap,
  activeTasks,
  completedTasks,
  coordinator,
  deletionCoordinator,
  createListVisible,
  setCreateListVisible,
  moveTarget,
  setMoveTarget,
  manageVisible,
  setManageVisible,
  shareTarget,
  setShareTarget,
  deleteTarget,
  setDeleteTarget,
  setActiveListId,
  setHighlightTaskId,
  createList,
  updateList,
  deleteList,
  reorderLists,
}: TodoListModalsProps) {
  return (
    <>
      <CreateListSheet
        visible={createListVisible}
        onDismiss={() => setCreateListVisible(false)}
        onSubmit={async (name) => {
          const created = await createList({ name });
          setActiveListId(created.id);
        }}
      />

      <MoveToListModal
        visible={!!moveTarget}
        lists={lists}
        currentListId={activeListId}
        atCap={atCap}
        onSelectList={async (targetListId) => {
          const task = moveTarget;
          setMoveTarget(null);
          if (task) {
            await coordinator.move(task.id, targetListId);
            setHighlightTaskId(task.id);
            setTimeout(() => setHighlightTaskId(null), 900);
          }
        }}
        onAddList={async (name) => {
          const created = await createList({ name });
          return created.id;
        }}
        onDismiss={() => setMoveTarget(null)}
      />

      <ManageListsModal
        visible={manageVisible}
        lists={lists}
        currentUserId={currentUserId}
        atCap={atCap}
        onRename={async (listId, newName) => {
          await updateList(listId, { name: newName });
        }}
        onRequestShare={(list) => setShareTarget(list)}
        onRequestDelete={(list) => setDeleteTarget(list)}
        onRequestAddList={() => setCreateListVisible(true)}
        onReorder={(ids) => reorderLists(ids)}
        onDismiss={() => setManageVisible(false)}
      />

      <ShareListMemberPicker
        visible={!!shareTarget}
        list={shareTarget}
        onSave={async (participantUserIds) => {
          if (!shareTarget) return;
          await updateList(shareTarget.id, { participantUserIds });
        }}
        onDismiss={() => setShareTarget(null)}
      />

      <DeleteListConfirmModal
        visible={!!deleteTarget}
        list={deleteTarget}
        otherLists={lists.filter((l) => l.id !== deleteTarget?.id)}
        taskCount={
          deleteTarget
            ? [...activeTasks, ...completedTasks].filter(
                (t) => t.listId === deleteTarget.id,
              ).length
            : 0
        }
        onConfirm={async (action) => {
          const target = deleteTarget;
          if (!target) return;

          // Without a coordinator (signed-out / no client) fall back to the
          // awaited path so we don't lose the existing behavior.
          if (!deletionCoordinator) {
            await deleteList(target.id, action);
            if (target.id === activeListId && defaultListId) {
              setActiveListId(defaultListId);
            }
            setDeleteTarget(null);
            return;
          }

          const tasksInDeletedList = [...activeTasks, ...completedTasks].filter(
            (t) => t.listId === target.id,
          );

          if (action.action === 'move') {
            // Land the user on the destination list immediately so they see
            // the moved tasks the instant the modal closes.
            setActiveListId(action.targetListId);
            setDeleteTarget(null);
            void deletionCoordinator.deleteWithMove({
              deletedListId: target.id,
              targetListId: action.targetListId,
              movedTasks: tasksInDeletedList,
            });
          } else {
            if (target.id === activeListId && defaultListId) {
              setActiveListId(defaultListId);
            }
            setDeleteTarget(null);
            void deletionCoordinator.deleteAll({ deletedListId: target.id });
          }
        }}
        onDismiss={() => setDeleteTarget(null)}
      />
    </>
  );
}
